/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

export type Environment = 'local' | 'testing' | 'staging' | 'production';

function resolveEnvVar(name: string): string | undefined {
  const windowEnv = typeof window !== 'undefined' && (window as any).__ENV__;
  return (windowEnv && windowEnv[name]) || import.meta.env[name] || undefined;
}

export const ENVIRONMENT: Environment =
  (resolveEnvVar('ENVIRONMENT') as Environment) || 'local';

export const isProduction = ENVIRONMENT === 'production';
export const isNonProduction = !isProduction;

export const isDevelopment = () => import.meta.env.DEV;

/**
 * Whether Sign in with Apple is enabled for the current stage.
 *
 * Gated behind the `APPLE_SIGNIN_ENABLED` runtime flag (injected per-stage via
 * `window.__ENV__` / `config.js`). The feature is DISABLED BY DEFAULT: unless
 * a stage explicitly sets the flag to a truthy value, the Apple button is
 * hidden and the social-login ordering falls back to Google-only. Mirrors the
 * backend `APPLE_SIGNIN_ENABLED` master gate so the UI never offers a provider
 * the backend will not serve.
 *
 * @returns `true` only when `APPLE_SIGNIN_ENABLED` is an explicit truthy value.
 */
export const isAppleSignInEnabled = (): boolean => {
  const raw = (resolveEnvVar('APPLE_SIGNIN_ENABLED') || '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
};
